import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

/**
 * Quem recebe avisos quando um cliente entra no lobby de vídeo (tipo 45).
 * Chatbot e agente de IA não se aplicam a este canal.
 */
export default function TwilioVideoRoutingSection({
  t,
  formData,
  setFormData,
  idChatbotRef,
  idAgenteRef,
  loadingUsers,
  activeHumanUsers,
  allHumanUsers,
  hasHumanRouting,
}) {
  const humanOptions = activeHumanUsers.length ? activeHumanUsers : allHumanUsers;
  const selectedHumanId = useMemo(() => {
    const fromList = Array.isArray(formData.idusuarios)
      ? formData.idusuarios.map(Number).find((n) => n > 0)
      : null;
    if (fromList) return fromList;
    const legacy = Number(formData.idusuario);
    return Number.isFinite(legacy) && legacy > 0 ? legacy : null;
  }, [formData.idusuarios, formData.idusuario]);

  const modeDerived = selectedHumanId ? "human" : "queue";
  const [mode, setMode] = useState(modeDerived);

  // Rehidrata ao abrir outro canal; não força “fila” se o usuário escolheu humano e ainda não selecionou.
  useEffect(() => {
    setMode(selectedHumanId ? "human" : "queue");
  }, [formData?.id]);

  const clearNonHumanRouting = () => {
    idChatbotRef.current = null;
    idAgenteRef.current = null;
  };

  // Chatbot/IA neste canal só atrapalham (podem bloquear aviso humano).
  useEffect(() => {
    clearNonHumanRouting();
    setFormData((prev) => {
      if (
        (prev.id_chatbot == null || String(prev.id_chatbot).trim() === "") &&
        (prev.id_agente == null || String(prev.id_agente).trim() === "") &&
        prev._twilioVideoRoutingMode
      ) {
        return prev;
      }
      return {
        ...prev,
        id_chatbot: null,
        id_agente: null,
        _twilioVideoRoutingMode: modeDerived,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount for video channel
  }, []);

  const onModeChange = (next) => {
    setMode(next);
    clearNonHumanRouting();
    if (next === "queue") {
      setFormData((prev) => ({
        ...prev,
        id_chatbot: null,
        id_agente: null,
        idusuarios: [],
        idusuario: null,
        _twilioVideoRoutingMode: "queue",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      id_chatbot: null,
      id_agente: null,
      idusuarios: selectedHumanId ? [selectedHumanId] : [],
      idusuario: selectedHumanId || null,
      _twilioVideoRoutingMode: "human",
    }));
  };

  const practiceLines = useMemo(() => {
    if (mode === "human" && selectedHumanId) {
      const human = humanOptions.find((u) => Number(u.id) === Number(selectedHumanId));
      const name = human ? titleOf(human) : `#${selectedHumanId}`;
      return [
        t("channelForm.twilioVideo.practiceHuman", {
          defaultValue:
            "Quando alguém entrar no lobby, {{name}} recebe o aviso e pode abrir a solicitação para entrar na chamada.",
          name,
        }),
        t(
          "channelForm.twilioVideo.practiceHumanWait",
          "O cliente fica aguardando na sala de vídeo até um atendente entrar pelo card na conversa."
        ),
      ];
    }
    if (mode === "human" && !selectedHumanId) {
      return [
        t(
          "channelForm.twilioVideo.practiceHumanPick",
          "Escolha o atendente que deve receber o aviso, ou mude para “Fila da equipe”."
        ),
      ];
    }
    return [
      t(
        "channelForm.twilioVideo.practiceQueue",
        "Sem responsável fixo: a solicitação entra na fila. Quem tiver permissão neste tipo/empresa vê o aviso e pode atender."
      ),
      t(
        "channelForm.twilioVideo.practiceQueueWait",
        "O cliente permanece na sala de vídeo até alguém entrar pela solicitação no chat."
      ),
    ];
  }, [humanOptions, mode, selectedHumanId, t]);

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {t("channelForm.twilioVideo.routingTitle", "Quem atende a chamada de vídeo")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            "channelForm.twilioVideo.routingIntro",
            "O cliente abre o link do lobby, informa os dados e espera na sala. Aqui você define quem recebe o aviso no Xpro para entrar na chamada."
          )}
        </Typography>

        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip size="small" label="1" color="primary" />
            <Typography variant="subtitle2" fontWeight={700}>
              {t("channelForm.twilioVideo.stepTitle", "Destino do aviso")}
            </Typography>
          </Stack>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={mode} onChange={(e) => onModeChange(e.target.value)}>
              <FormControlLabel
                value="human"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t(
                        "channelForm.twilioVideo.destHuman",
                        "Atendente específico"
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "channelForm.twilioVideo.destHumanHint",
                        "Só essa pessoa recebe o aviso (ideal para um responsável fixo)."
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 0.5 }}
              />
              <FormControlLabel
                value="queue"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t("channelForm.twilioVideo.destQueue", "Fila da equipe")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "channelForm.twilioVideo.destQueueHint",
                        "Sem responsável fixo — quem tem permissão no tipo/empresa pode atender."
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start" }}
              />
            </RadioGroup>
          </FormControl>

          {mode === "human" && (
            <Box sx={{ mt: 1.5, maxWidth: 480 }}>
              <TextField
                select
                fullWidth
                required
                label={t(
                  "channelForm.twilioVideo.pickHuman",
                  "Qual atendente recebe o aviso?"
                )}
                value={selectedHumanId ?? ""}
                disabled={loadingUsers}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  clearNonHumanRouting();
                  if (!Number.isFinite(id) || id <= 0) {
                  setFormData((prev) => ({
                    ...prev,
                    id_chatbot: null,
                    id_agente: null,
                    idusuarios: [],
                    idusuario: null,
                    _twilioVideoRoutingMode: "human",
                  }));
                  return;
                }
                setFormData((prev) => ({
                  ...prev,
                  id_chatbot: null,
                  id_agente: null,
                  idusuarios: [id],
                  idusuario: id,
                  _twilioVideoRoutingMode: "human",
                }));
              }}
              helperText={t(
                "channelForm.twilioVideo.pickHumanHelper",
                "Apenas um atendente — o canal de vídeo notifica um responsável por vez."
              )}
              error={mode === "human" && !hasHumanRouting}
            >
                <MenuItem value="">
                  {t("channelForm.twilioVideo.pickHumanEmpty", "Selecione…")}
                </MenuItem>
                {loadingUsers && (
                  <MenuItem value="" disabled>
                    {t("channelForm.twilioVideo.loadingUsers", "Carregando…")}
                  </MenuItem>
                )}
                {!loadingUsers &&
                  humanOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {titleOf(u)}
                      {u?.email ? ` — ${u.email}` : ""}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
          )}

          {mode === "queue" && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t(
                "channelForm.twilioVideo.queueInfo",
                "Confirme que a equipe tem permissão no Tipo e na Empresa deste canal — senão ninguém verá a solicitação na fila."
              )}
            </Alert>
          )}
        </Box>

        <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
            {t("channelForm.twilioVideo.practiceTitle", "Na prática")}
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            {practiceLines.map((line) => (
              <Typography component="li" variant="body2" key={line}>
                {line}
              </Typography>
            ))}
          </Stack>
        </Alert>

        <Typography variant="caption" color="text.secondary" display="block">
          {t(
            "channelForm.twilioVideo.notApplicableNote",
            "Chatbot e agente de IA não se aplicam a chamadas de vídeo — eles não entram na sala e podem impedir o aviso aos atendentes."
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {t(
            "channelForm.twilioVideo.tipoStillBelow",
            "O Tipo de solicitação padrão (e subtipos) continua nos campos abaixo — use-os para a fila e permissões corretas."
          )}
        </Typography>
      </Paper>
    </Grid>
  );
}

/** No save: remove chatbot/IA e campos só de UI; um único humano ou fila. */
export function sanitizeTwilioVideoRouting(formData) {
  const { _twilioVideoRoutingMode, ...rest } = formData;
  void _twilioVideoRoutingMode;
  const next = { ...rest, id_chatbot: null, id_agente: null };
  const ids = Array.isArray(next.idusuarios)
    ? next.idusuarios.map(Number).filter((n) => n > 0)
    : [];
  const primary = ids[0] || (Number(next.idusuario) > 0 ? Number(next.idusuario) : null);
  if (primary) {
    next.idusuarios = [primary];
    next.idusuario = primary;
  } else {
    next.idusuarios = [];
    next.idusuario = null;
  }
  return next;
}

export function validateTwilioVideoRouting(formData, { t }) {
  const ids = Array.isArray(formData.idusuarios)
    ? formData.idusuarios.map(Number).filter((n) => n > 0)
    : [];
  const hasHuman = ids.length > 0 || Number(formData.idusuario) > 0;
  const mode =
    formData._twilioVideoRoutingMode || (hasHuman ? "human" : "queue");

  if (mode === "human" && !hasHuman) {
    return t(
      "channelForm.twilioVideo.errPickHuman",
      "Selecione o atendente que receberá o aviso, ou mude para “Fila da equipe”."
    );
  }
  return null;
}
